import _memoize from 'lodash/memoize';
import { getFont, FONT } from '@tornado-rdk/typography';
import { PRIMARY, makeThemeColor, DEFAULT_MODE } from '@tornado-rdk/theme';
import { FULL_HD, HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { cacheResolver, makeDefaultStylingContext } from '../utils/style';
import {
  BORDER_RADIUS,
  BORDER_WIDTH,
  SCALE_FOCUSED,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import {
  TITLE_FONT_NAME,
  TITLE_FONT_TYPE,
  templates,
  imageOptions,
  sizes,
  BORDER_COLOR,
  INNER_BORDER_COLOR,
} from './horizontalSquare.constants';
import { TRANSPARENT } from '../utils/colors';

export const defaultContext = makeDefaultStylingContext();

export const PADDING = {
  [HD]: 16,
  [FULL_HD]: 24,
};

export const TEXT_GAP = {
  [HD]: 4,
  [FULL_HD]: 4,
};

export const TEXT_LEFT_PADDING = {
  [HD]: 24,
  [FULL_HD]: 24,
};

export const IMAGE_OFFSET = {
  [HD]: BORDER_WIDTH[HD] * 2,
  [FULL_HD]: BORDER_WIDTH[FULL_HD] * 2,
};

export const getImageSize = screenSize =>
  imageOptions[templates.HorizontalSquare][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY.height;

export const colors = {
  background: makeThemeColor(PRIMARY, 0.1),
  border: BORDER_COLOR,
  innerBorder: INNER_BORDER_COLOR,
  text: makeThemeColor(PRIMARY),
};

export const makeBasicTextStyle = ({ font, colorMode = DEFAULT_MODE }) => ({
  color: colors.text[colorMode],
  text: {
    ...font,
    verticalAlign: 'middle',
    wordWrap: false,
    textOverflow: '...',
  },
});

export const makeBasicIconStyle = () => ({
  alpha: 0.8,
});

export const getInitialStyle = ({ screenSize = FULL_HD, colorMode = DEFAULT_MODE } = defaultContext) => {
  const { w, h } = sizes[templates.HorizontalSquare][RENDERING_CONTEXT.GENERAL][screenSize];
  const gap = TEXT_GAP[screenSize];
  const imageSize = getImageSize(screenSize);
  const verticalOffset = Math.round((h - imageSize) / 2);

  return {
    Background: {
      alpha: 0,
      w,
      h,
      color: colors.background[colorMode],
      shader: {
        radius: 8,
      },
    },
    Border: {
      alpha: 0,
      x: PADDING[screenSize] - BORDER_WIDTH[screenSize],
      y: verticalOffset - BORDER_WIDTH[screenSize],
      w: imageSize + IMAGE_OFFSET[screenSize],
      h: imageSize + IMAGE_OFFSET[screenSize],
      shader: {
        outline: BORDER_WIDTH[screenSize],
        radius: BORDER_RADIUS[screenSize],
        strokeColor: TRANSPARENT,
      },
    },
    ScalableContent: {
      x: PADDING[screenSize],
      y: verticalOffset,
      w: imageSize,
      h: imageSize,
      shader: {
        stroke: 0,
        strokeColor: colors.border[colorMode],
        radius: BORDER_RADIUS[screenSize],
      },
    },
    FlexContainer: {
      flex: {
        direction: 'column',
        justifyContent: 'center',
      },
      x: PADDING[screenSize] + imageSize + TEXT_LEFT_PADDING[screenSize],
      y: verticalOffset,
      h: h - verticalOffset * 2,
      Line1: {
        ...makeBasicTextStyle({
          font: getFont(TITLE_FONT_NAME, screenSize, TITLE_FONT_TYPE),
          colorMode,
        }),
        flexItem: {
          marginBottom: gap,
        },
      },
      Line2: {
        ...makeBasicTextStyle({
          font: getFont(FONT.BERLIN_1, screenSize),
          colorMode,
        }),
        flexItem: {
          marginBottom: gap,
        },
      },
      Line3: {
        text: {
          ...getFont(FONT.BERLIN_1, screenSize),
          maxLines: 2,
          verticalAlign: 'middle',
          wordWrap: true,
          maxLinesSuffix: '...',
        },
        flexItem: {
          marginBottom: gap,
        },
      },
      ProgressBar: {
        flexItem: {
          marginTop: 16,
        },
      },
    },
  };
};

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    Background: {
      smooth: {
        alpha: 1,
      },
    },
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': colors.border[colorMode],
        'shader.innerColor': colors.innerBorder[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    ScalableContent: {
      smooth: {
        scale: 1.05,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  Background: {
    smooth: {
      alpha: 0,
    },
  },
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  ScalableContent: {
    smooth: {
      scale: 1,
    },
  },
};
